import axios from 'axios';

// Создаем экземпляр axios с базовым URL
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Добавляем перехватчик для добавления токена к запросам
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Добавляем перехватчик для обработки ошибок авторизации
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      // Если сервер вернул 401, значит токен истек или недействителен
      // Удаляем токен и перенаправляем на страницу входа
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('expiresAt');
      
      // Определяем, какой тип пользователя был авторизован
      const isAdmin = localStorage.getItem('isAdmin') === 'true';
      localStorage.removeItem('isAdmin');
      
      // Перенаправляем на соответствующую страницу входа
      if (isAdmin) {
        window.location.href = '/admin/login';
      } else {
        window.location.href = '/cashier/login';
      }
    }
    return Promise.reject(error);
  }
);

// Сервис для работы с авторизацией
const AuthService = {
  // Авторизация администратора
  loginAdmin: async (username, password) => {
    try {
      const response = await api.post('/auth/admin/login', { username, password });
      const { token, expires_at, is_admin, role, allowed_sections } = response.data;
      
      // Сохраняем данные в localStorage
      localStorage.setItem('token', token);
      localStorage.setItem('expiresAt', expires_at);
      localStorage.setItem('isAdmin', is_admin);
      if (role) {
        localStorage.setItem('role', role);
      }
      localStorage.setItem('allowedSections', JSON.stringify(allowed_sections || []));
      localStorage.setItem('user', JSON.stringify({ username, is_admin, role }));
      
      return response.data;
    } catch (error) {
      console.error('Ошибка при авторизации администратора:', error);
      throw error;
    }
  },
  
  // Список активных кассиров для выпадающего списка на странице входа
  getCashierList: async () => {
    const response = await api.get('/auth/cashier/list');
    return response.data.cashiers || [];
  },

  // Авторизация кассира
  loginCashier: async (username, password) => {
    try {
      const response = await api.post('/auth/cashier/login', { username, password });
      const { token, expires_at, is_admin } = response.data;
      
      // Сохраняем данные в localStorage
      localStorage.setItem('token', token);
      localStorage.setItem('expiresAt', expires_at);
      localStorage.setItem('isAdmin', is_admin);
      localStorage.setItem('user', JSON.stringify({ username, is_admin }));
      
      return response.data;
    } catch (error) {
      console.error('Ошибка при авторизации кассира:', error);
      throw error;
    }
  },
  
  // Выход из системы
  logout: async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Ошибка при выходе из системы:', error);
    } finally {
      // Удаляем данные из localStorage
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('expiresAt');
      localStorage.removeItem('isAdmin');
      localStorage.removeItem('role');
      localStorage.removeItem('allowedSections');
    }
  },
  
  // Получение текущего пользователя
  getCurrentUser: () => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      return JSON.parse(userStr);
    }
    return null;
  },
  
  // Проверка, авторизован ли пользователь
  isAuthenticated: () => {
    const token = localStorage.getItem('token');
    const expiresAt = localStorage.getItem('expiresAt');
    
    if (!token || !expiresAt) {
      return false;
    }
    
    // Проверяем, не истек ли токен
    const expirationDate = new Date(expiresAt);
    const now = new Date();
    
    return now < expirationDate;
  },
  
  // Проверка, является ли пользователь администратором
  isAdmin: () => {
    const isAdmin = localStorage.getItem('isAdmin');
    return isAdmin === 'true';
  },
  
  // Роль пользователя (super_admin | limited_admin | '')
  getRole: () => {
    return localStorage.getItem('role') || '';
  },
  
  isLimitedAdmin: () => {
    return AuthService.getRole() === 'limited_admin';
  },

  // Разрешённые разделы (для limited_admin с персональной учёткой)
  getAllowedSections: () => {
    try {
      return JSON.parse(localStorage.getItem('allowedSections') || '[]');
    } catch (e) {
      return [];
    }
  },

  // Доступен ли раздел текущему админу.
  // super_admin — всё; legacy limited_admin без allowed_sections — всё (совместимость);
  // персональный limited_admin — только разделы из списка.
  canAccessSection: (section) => {
    const role = AuthService.getRole();
    if (role === 'super_admin') return true;
    const sections = AuthService.getAllowedSections();
    if (!sections || sections.length === 0) return true; // legacy общий limited_admin
    return sections.includes(section);
  },

  // Управление администраторами (только super_admin)
  getAdmins: async () => {
    const response = await api.get('/auth/admins');
    return response.data.admins || [];
  },
  createAdmin: async ({ username, password, display_name, allowed_sections }) => {
    const response = await api.post('/auth/admins', { username, password, display_name, allowed_sections });
    return response.data;
  },
  updateAdmin: async (id, data) => {
    const response = await api.put('/auth/admins', { ...data, id });
    return response.data;
  },

  // Белый список Wi-Fi: выход через туннель для конкретных устройств (только super_admin)
  getWifiVpnClients: async () => {
    const response = await api.get('/admin/wifi-vpn');
    return response.data.clients || [];
  },
  createWifiVpnClient: async ({ mac_address, comment }) => {
    const response = await api.post('/admin/wifi-vpn', { mac_address, comment });
    return response.data;
  },
  updateWifiVpnClient: async (id, data) => {
    const response = await api.put('/admin/wifi-vpn', { ...data, id });
    return response.data;
  },
  deleteWifiVpnClient: async (id) => {
    const response = await api.delete('/admin/wifi-vpn', { data: { id } });
    return response.data;
  },
  
  // Получение правильного пути для перенаправления после авторизации
  getRedirectPath: () => {
    const isAdmin = AuthService.isAdmin();
    return isAdmin ? '/admin' : '/cashier';
  },
  
  // Проверка, нужно ли перенаправить пользователя
  shouldRedirect: (currentPath) => {
    const isAuthenticated = AuthService.isAuthenticated();
    const isAdmin = AuthService.isAdmin();
    
    // Если пользователь не авторизован, не нужно перенаправлять
    if (!isAuthenticated) {
      return false;
    }
    
    // Если администратор находится на странице кассира, перенаправляем
    if (isAdmin && currentPath.startsWith('/cashier')) {
      return '/admin';
    }
    
    // Если кассир находится на странице администратора, перенаправляем
    if (!isAdmin && currentPath.startsWith('/admin')) {
      return '/cashier';
    }
    
    return false;
  },
  
  // Получение списка кассиров (только для администратора)
  getCashiers: async () => {
    try {
      const response = await api.get('/auth/cashiers');
      return response.data;
    } catch (error) {
      console.error('Ошибка при получении списка кассиров:', error);
      throw error;
    }
  },
  
  // Создание кассира (только для администратора)
  createCashier: async (username, password) => {
    try {
      const response = await api.post('/auth/cashiers', { username, password });
      return response.data;
    } catch (error) {
      console.error('Ошибка при создании кассира:', error);
      throw error;
    }
  },
  
  // Получение кассира по ID (только для администратора)
  getCashierById: async (id) => {
    try {
      const response = await api.get(`/auth/cashiers/by-id?id=${id}`);
      return response.data;
    } catch (error) {
      console.error('Ошибка при получении кассира по ID:', error);
      throw error;
    }
  },
  
  // Обновление кассира (только для администратора)
  updateCashier: async (id, data) => {
    try {
      const response = await api.put('/auth/cashiers', { ...data, id });
      return response.data;
    } catch (error) {
      console.error('Ошибка при обновлении кассира:', error);
      throw error;
    }
  },
  
  // Удаление кассира (только для администратора)
  deleteCashier: async (id) => {
    try {
      const response = await api.delete('/auth/cashiers', { data: { id } });
      return response.data;
    } catch (error) {
      console.error('Ошибка при удалении кассира:', error);
      throw error;
    }
  },

  // Методы для работы с уборщиками
  loginCleaner: async (username, password) => {
    try {
      const response = await api.post('/auth/cleaner/login', { username, password });
      const { token, expires_at, is_admin } = response.data;
      
      // Сохраняем данные в localStorage
      localStorage.setItem('token', token);
      localStorage.setItem('expiresAt', expires_at);
      localStorage.setItem('isAdmin', is_admin);
      localStorage.setItem('user', JSON.stringify({ username, is_admin }));
      
      return response.data;
    } catch (error) {
      console.error('Ошибка при авторизации уборщика:', error);
      throw error;
    }
  },

  createCleaner: async (cleanerData) => {
    try {
      const response = await api.post('/auth/cleaners', cleanerData);
      return response.data;
    } catch (error) {
      console.error('Ошибка при создании уборщика:', error);
      throw error;
    }
  },

  getCleaners: async () => {
    try {
      const response = await api.get('/auth/cleaners');
      return response.data;
    } catch (error) {
      console.error('Ошибка при получении списка уборщиков:', error);
      throw error;
    }
  },

  updateCleaner: async (cleanerData) => {
    try {
      const response = await api.put('/auth/cleaners', cleanerData);
      return response.data;
    } catch (error) {
      console.error('Ошибка при обновлении уборщика:', error);
      throw error;
    }
  },

  deleteCleaner: async (cleanerId) => {
    try {
      const response = await api.delete('/auth/cleaners', { data: { id: cleanerId } });
      return response.data;
    } catch (error) {
      console.error('Ошибка при удалении уборщика:', error);
      throw error;
    }
  },
};

export default AuthService;
